import React from 'react';
import styled, { css } from 'styled-components';
import { theme } from '../../styles/theme';

/**
 * Button component with variants
 * @param {Object} props - Component props
 * @param {string} [props.variant='primary'] - Button variant
 * @param {boolean} [props.isLoading=false] - Loading state
 * @param {boolean} [props.fullWidth=false] - Full width button
 * @param {string} [props.size='medium'] - Button size
 * @param {Function} props.onClick - Click handler
 */
const Button = ({
  children,
  variant = 'primary',
  isLoading = false,
  disabled = false,
  fullWidth = false,
  size = 'medium',
  type = 'button',
  onClick,
  ...rest
}) => {
  return (
    <ButtonWrapper
      type={type}
      variant={variant}
      disabled={disabled || isLoading}
      fullWidth={fullWidth}
      size={size}
      onClick={onClick}
      {...rest}
    >
      {isLoading ? <LoadingSpinner /> : children}
    </ButtonWrapper>
  );
};

const variantStyles = {
  primary: css`
    background-color: ${theme.colors.primary};
    color: white;
    
    &:hover:not(:disabled) {
      background-color: ${theme.colors.primaryHover};
    }
  `,
  
  secondary: css`
    background-color: transparent;
    color: ${theme.colors.primary};
    border: 1px solid ${theme.colors.primary};
    
    &:hover:not(:disabled) {
      background-color: ${theme.colors.backgroundAlt};
    }
  `,
  
  danger: css`
    background-color: ${theme.colors.danger};
    color: white;
    
    &:hover:not(:disabled) {
      background-color: #b91c1c;
    }
  `,
  
  ghost: css`
    background-color: transparent;
    color: ${theme.colors.text};
    
    &:hover:not(:disabled) {
      background-color: ${theme.colors.backgroundAlt};
    }
  `,
};

const sizeStyles = {
  small: css`
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
  `,
  
  medium: css`
    padding: 0.5rem 1rem;
    font-size: 1rem;
  `,
  
  large: css`
    padding: 0.75rem 1.5rem;
    font-size: 1.125rem;
  `,
};

const ButtonWrapper = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${theme.borderRadius.md};
  font-weight: 500;
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  border: none;
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
  
  ${({ variant }) => variantStyles[variant] || variantStyles.primary}
  ${({ size }) => sizeStyles[size] || sizeStyles.medium}
  
  &:disabled {
    background-color: ${theme.colors.disabled};
    color: ${theme.colors.disabledText};
    cursor: not-allowed;
    border-color: ${theme.colors.disabled};
  }
  
  /* Add space between icons and text */
  svg {
    margin-right: ${({ iconOnly }) => (iconOnly ? '0' : '0.5rem')};
  }
`;

const Spinner = styled.div`
  width: 1rem;
  height: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 0.8s linear infinite;
  margin-right: 0.5rem;
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingSpinner = () => {
  return <Spinner />;
};

export default Button;
